/**
 * ErrorBoundary Component
 *
 * React Error Boundary that catches and handles unhandled errors in the component tree.
 * Displays fallback UI and provides recovery options.
 *
 * Features:
 * - Catches React component errors
 * - Displays user-friendly error messages
 * - Provides reset/retry functionality
 * - Logs errors for debugging
 * - Supports custom fallback UI
 * - Auto-reset on prop changes (resetKeys)
 *
 * Phase 6.3: Error Handling & Edge Cases
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { COPY } from '../copy';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when error is caught
   */
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details when error is caught
   */
  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Error caught:', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state for display
    this.setState({
      errorInfo,
    });

    // Call custom error callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error boundary when resetKeys change
   */
  componentDidUpdate(prevProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    if (
      hasError &&
      resetKeys &&
      prevProps.resetKeys &&
      resetKeys !== prevProps.resetKeys
    ) {
      // Reset error state when resetKeys change
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
    }
  }

  /**
   * Reset error state (for manual retry)
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Determine error type from error object
   */
  getErrorType(error) {
    if (!error) return 'unknown';

    const message = error.message?.toLowerCase() || '';

    if (error.name === 'NetworkError' || message.includes('network')) {
      return 'network';
    }

    if (message.includes('timeout')) {
      return 'timeout';
    }

    if (message.includes('session')) {
      return 'session';
    }

    return 'generic';
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    // Normal rendering when no error
    if (!hasError) {
      return children;
    }

    // Use custom fallback UI if provided
    if (fallback) {
      return fallback;
    }

    // Default error UI
    const errorType = this.getErrorType(error);
    const errorMessage = error?.message || 'An unexpected error occurred';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          {/* Error Icon */}
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 rounded-full p-4">
              <svg
                className="w-12 h-12 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          {/* Error Title */}
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
            {COPY.errors.boundary.title}
          </h2>

          {/* Error Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>

          {/* Error Type Specific Message */}
          {errorType === 'network' && (
            <p className="text-gray-600 text-center mb-6">
              {COPY.errors.boundary.types.network.message}
              <br />
              <span className="text-sm">{COPY.errors.boundary.types.network.action}</span>
            </p>
          )}

          {errorType === 'timeout' && (
            <p className="text-gray-600 text-center mb-6">
              {COPY.errors.boundary.types.timeout.message}
              <br />
              <span className="text-sm">{COPY.errors.boundary.types.timeout.action}</span>
            </p>
          )}

          {errorType === 'session' && (
            <p className="text-gray-600 text-center mb-6">
              {COPY.errors.boundary.types.session.message}
              <br />
              <span className="text-sm">{COPY.errors.boundary.types.session.action}</span>
            </p>
          )}

          {errorType === 'generic' && (
            <p className="text-gray-600 text-center mb-6">
              {COPY.errors.boundary.types.generic.message}
              <br />
              <span className="text-sm">{COPY.errors.boundary.types.generic.action}</span>
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Try Again Button */}
            <button
              onClick={this.handleReset}
              className="w-full bg-primary-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors"
            >
              {COPY.errors.boundary.buttons.tryAgain}
            </button>

            {/* Home Link */}
            <a
              href="/"
              className="w-full bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors text-center"
            >
              {COPY.errors.boundary.buttons.goHome}
            </a>
          </div>

          {/* Developer Info (only in development) */}
          {import.meta.env.DEV && error && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                {COPY.errors.boundary.devDetails}
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-40">
                <pre className="whitespace-pre-wrap">{error.stack}</pre>
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  resetKeys: PropTypes.array,
};

ErrorBoundary.defaultProps = {
  fallback: null,
  onError: null,
  resetKeys: null,
};

export default ErrorBoundary;
